// Types pour les clients
export type ClientType = "regular" | "new" | "returning"

export type Client = {
  id: string
  firstName: string
  lastName: string
  email: string
  phone?: string
  dateOfBirth?: string
  registrationDate: string
  lastVisit: string
  totalSpent: number
  visitCount: number
  type: ClientType
  favoriteServices: string[]
  fidelityPoints: number
}

// Types pour le tri
export type SortField = keyof Client | "fullName"
export type SortDirection = "asc" | "desc" | null

export type SortState = {
  field: SortField | null
  direction: SortDirection
}

// Fonction utilitaire pour trier les clients
export const sortClients = (
  clients: Client[],
  field: SortField,
  direction: SortDirection
): Client[] => {
  if (!direction) return clients

  const sorted = [...clients].sort((a, b) => {
    let aValue: string | number | Date
    let bValue: string | number | Date

    switch (field) {
      case "fullName":
        aValue = `${a.firstName} ${a.lastName}`.toLowerCase()
        bValue = `${b.firstName} ${b.lastName}`.toLowerCase()
        break
      case "email":
        aValue = a.email.toLowerCase()
        bValue = b.email.toLowerCase()
        break
      case "visitCount":
        aValue = a.visitCount
        bValue = b.visitCount
        break
      case "totalSpent":
        aValue = a.totalSpent
        bValue = b.totalSpent
        break
      case "lastVisit":
        aValue = new Date(a.lastVisit).getTime()
        bValue = new Date(b.lastVisit).getTime()
        break
      case "fidelityPoints":
        aValue = a.fidelityPoints
        bValue = b.fidelityPoints
        break
      case "type":
        aValue = a.type
        bValue = b.type
        break
      default:
        aValue = String(a[field as keyof Client])
        bValue = String(b[field as keyof Client])
    }

    if (typeof aValue === "string" && typeof bValue === "string") {
      return direction === "asc"
        ? aValue.localeCompare(bValue, "fr", { numeric: true })
        : bValue.localeCompare(aValue, "fr", { numeric: true })
    }

    if (typeof aValue === "number" && typeof bValue === "number") {
      return direction === "asc" ? aValue - bValue : bValue - aValue
    }

    // Fallback pour autres types
    if (aValue < bValue) return direction === "asc" ? -1 : 1
    if (aValue > bValue) return direction === "asc" ? 1 : -1
    return 0
  })

  return sorted
}

export const getTypeLabel = (type: ClientType) => {
  switch (type) {
    case "new":
      return "Nouveau"
    case "regular":
      return "Régulier"
    case "returning":
      return "De retour"
    default:
      return type
  }
}

export const getTypeColor = (type: ClientType) => {
  switch (type) {
    case "new":
      return "bg-green-100 text-green-700 border-green-200 hover:bg-green-200"
    case "regular":
      return "bg-blue-100 text-blue-700 border-blue-200 hover:bg-blue-200"
    case "returning":
      return "bg-orange-100 text-orange-700 border-orange-200 hover:bg-orange-200"
    default:
      return "bg-gray-100 text-gray-700 border-gray-200 hover:bg-gray-200"
  }
}
